import IndexStore from 'src/stores/index';
import type {SenseEvent} from 'src/types/events';
import type {
  Question,
  QuestionType,
  MultipleChoiceQuestion,
  RatingScaleQuestion,
  NPSSurveyQuestion,
  RhetoricalQuestion,
  BeeFreeModule,
  SMSRhetoricalQuestion,
} from 'src/types/survey';

import uniqueId from 'lodash/uniqueId';
import {fillArray} from 'src/utils/array';

import {EventTypeEnum} from 'src/stores/event';

import {selectAgencyConfig} from 'src/selectors/agency';


type Event = SenseEvent;

export function populateEventModules(
  store: IndexStore,
  editEvent: Partial<SenseEvent>,
): SenseEvent {
  const atsFieldWritebackEnabled = selectAgencyConfig(
    // @ts-expect-error - TS2339 - Property 'reduxStore' does not exist on type 'IndexStore'.
    store.reduxStore.getState(),
  )['atsFieldWritebackEnabled'];
  const agencyQuestionPrototypes = questionPrototypes(store);

  // TODO (kyle): NPS and SMS_NPS are not true EventType strings
  if (editEvent.eventType === EventTypeEnum.NPS) {
    // @ts-expect-error - TS2345 - Argument of type 'Partial<SenseEvent>' is not assignable to parameter of type 'SenseEvent'.
    editEvent = makeEventNps(agencyQuestionPrototypes, editEvent);
  } else if (editEvent.eventType === EventTypeEnum.SMS_NPS) {
    // @ts-expect-error - TS2345 - Argument of type 'Partial<SenseEvent>' is not assignable to parameter of type 'SenseEvent'.
    editEvent = makeEventSMSNps(agencyQuestionPrototypes, editEvent);
  } else if (editEvent.eventType === EventTypeEnum.Survey) {
    editEvent = makeEventSurvey(
      agencyQuestionPrototypes,
      // @ts-expect-error - TS2345 - Argument of type 'Partial<SenseEvent>' is not assignable to parameter of type 'SenseEvent'.
      editEvent,
      atsFieldWritebackEnabled,
    );
  } else if (editEvent.eventType === EventTypeEnum.SMS_Survey) {
    editEvent = makeEventSMSSurvey(
      agencyQuestionPrototypes,
      // @ts-expect-error - TS2345 - Argument of type 'Partial<SenseEvent>' is not assignable to parameter of type 'SenseEvent'.
      editEvent,
      atsFieldWritebackEnabled,
    );
  } else if (editEvent.eventType === EventTypeEnum.SMS_Job) {
    // @ts-expect-error - TS2345 - Argument of type 'Partial<SenseEvent>' is not assignable to parameter of type 'SenseEvent'.
    editEvent = makeEventSmsJobMessage(agencyQuestionPrototypes, editEvent);
  } else if (editEvent.eventType === EventTypeEnum.SMS_Message) {
    // @ts-expect-error - TS2345 - Argument of type 'Partial<SenseEvent>' is not assignable to parameter of type 'SenseEvent'.
    editEvent = makeEventSmsMessage(agencyQuestionPrototypes, editEvent);
  } else if (editEvent.eventType === EventTypeEnum.Message) {
    // @ts-expect-error - TS2345 - Argument of type 'Partial<SenseEvent>' is not assignable to parameter of type 'SenseEvent'.
    editEvent = makeEventMessage(agencyQuestionPrototypes, editEvent);
  } else if (editEvent.eventType === EventTypeEnum.List) {
    // @ts-expect-error - TS2345 - Argument of type 'Partial<SenseEvent>' is not assignable to parameter of type 'SenseEvent'.
    editEvent = makeEventList(agencyQuestionPrototypes, editEvent);
  } else if (editEvent.eventType === EventTypeEnum.BeeFree_Email) {
    // @ts-expect-error - TS2345 - Argument of type 'Partial<SenseEvent>' is not assignable to parameter of type 'SenseEvent'.
    editEvent = makeBeeFreeMessage(agencyQuestionPrototypes, editEvent);
  } else if (editEvent.eventType === EventTypeEnum.SMS_Chatbot) {
    // @ts-expect-error - TS2345 - Argument of type 'Partial<SenseEvent>' is not assignable to parameter of type 'SenseEvent'.
    editEvent = makeEventSmsChatbot(agencyQuestionPrototypes, editEvent);
  }

  // @ts-expect-error - TS2322 - Type 'Partial<SenseEvent>' is not assignable to type 'SenseEvent'.
  return editEvent;
}

const makeEventNps = (
  prototypes: PrototypeQuestions,
  event: SenseEvent,
): SenseEvent => ({
  ...event,
  type: EventTypeEnum.Survey,
  eventType: EventTypeEnum.NPS,
  // @ts-expect-error - TS2322 - Type 'Question | undefined' is not assignable to type 'Question'.
  questions: [prototypes['nps_survey_question']],
});

const makeEventSMSNps = (
  prototypes: PrototypeQuestions,
  event: SenseEvent,
): SenseEvent => ({
  ...event,
  type: EventTypeEnum.SMS_Survey,
  eventType: EventTypeEnum.SMS_Survey,
  questions: [
    // @ts-expect-error - TS2322 - Type 'Question | undefined' is not assignable to type 'Question'.
    prototypes['sms_message_survey_module'],
    // @ts-expect-error - TS2322 - Type 'Question | undefined' is not assignable to type 'Question'.
    prototypes['nps_survey_question'],
  ],
});

const makeEventSurvey = (
  prototypes: PrototypeQuestions,
  event: Event,
  atsFieldWritebackEnabled: boolean,
): Event =>
  atsFieldWritebackEnabled
    ? {
        ...event,
        // @ts-expect-error - TS2322 - Type 'Question | undefined' is not assignable to type 'Question'.
        questions: [prototypes['message_survey_module']],
      }
    : {
        ...event,
        questions: [
          // @ts-expect-error - TS2322 - Type 'Question | undefined' is not assignable to type 'Question'.
          prototypes['message_survey_module'],
          // @ts-expect-error - TS2322 - Type 'Question | undefined' is not assignable to type 'Question'.
          prototypes['rating_scale_survey_question'],
        ],
      };

const makeEventSMSSurvey = (
  prototypes: PrototypeQuestions,
  event: Event,
  atsFieldWritebackEnabled: boolean,
): Event =>
  atsFieldWritebackEnabled
    ? {
        ...event,
        // @ts-expect-error - TS2322 - Type 'Question | undefined' is not assignable to type 'Question'.
        questions: [prototypes['sms_message_survey_module']],
      }
    : {
        ...event,
        questions: [
          // @ts-expect-error - TS2322 - Type 'Question | undefined' is not assignable to type 'Question'.
          prototypes['sms_message_survey_module'],
          // @ts-expect-error - TS2322 - Type 'Question | undefined' is not assignable to type 'Question'.
          prototypes['rating_scale_survey_question'],
        ],
      };

const makeEventSmsChatbot = (
  prototypes: PrototypeQuestions,
  event: Event,
): Event => ({
  ...event,
  questions: [],
});

const makeEventSmsMessage = (
  prototypes: PrototypeQuestions,
  event: Event,
): Event => ({
  ...event,
  // @ts-expect-error - TS2322 - Type 'Question | undefined' is not assignable to type 'Question'.
  questions: [prototypes['sms_message_survey_module']],
});
const makeEventSmsJobMessage = (
  prototypes: PrototypeQuestions,
  event: Event,
): Event => ({
  ...event,
  questions: [
    // @ts-expect-error - TS2322 - Type '{ text: string; includes_job_match_variables: true; send_alt_text: true; alt_text: string; } | { text: string; includes_job_match_variables: true; send_alt_text: true; alt_text: string; questionTypeUpdated?: boolean | undefined; ... 11 more ...; alertRating?: number | undefined; } | ... 7 more ... | { ...; }' is not assignable to type 'Question'.
    {
      ...prototypes['sms_message_survey_module'],
      text: 'I found a job that looks like a great match for you: <matched_job/company_name> need a <matched_job/title> in <matched_job/location>. Reply if you want to learn more!',
      includes_job_match_variables: true,
      send_alt_text: true,

      alt_text:
        'I may have some jobs that would be a great fit for you! If you’re still available, please reply!',
    },
  ],
});

const makeEventMessage = (
  prototypes: PrototypeQuestions,
  event: Event,
): Event => ({
  ...event,
  // @ts-expect-error - TS2322 - Type 'Question | undefined' is not assignable to type 'Question'.
  questions: [prototypes['message_survey_module']],
});

const makeBeeFreeMessage = (
  prototypes: PrototypeQuestions,
  event: Event,
): Event => ({
  ...event,
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type '"beefree_module"' can't be used to index type 'Partial<Record<QuestionType, Question>>'.
  questions: [prototypes['beefree_module']],
});

const makeEventList = (
  prototypes: PrototypeQuestions,
  event: Event,
): Event => ({
  ...event,
  questions: [
    // @ts-expect-error - TS2322 - Type 'Question | undefined' is not assignable to type 'Question'.
    prototypes.message_survey_module,
    // @ts-expect-error - TS2322 - Type 'Question | undefined' is not assignable to type 'Question'.
    prototypes.attribute_list_module,
    // @ts-expect-error - TS2322 - Type 'Question | undefined' is not assignable to type 'Question'.
    prototypes.message_survey_module,
  ],
});

export type PrototypeQuestions = Partial<Record<QuestionType, Question>>;
export const questionPrototypes = (store: IndexStore): PrototypeQuestions => {
  // @ts-expect-error - TS2339 - Property 'reduxStore' does not exist on type 'IndexStore'.
  const {agency} = store.reduxStore.getState().agency;

  // this function consumes the store to allow individual agencies
  // to have custom prototype questions (i.e. 1-5 rating scales)
  // if it's not a global preference yet.
  const args: Record<string, any> = {};

  if (['apex', 'agency-1'].includes(agency.name)) {
    args.minRating = 0;
    args.maxRating = 10;
  }
  if (
    [
      'judge-staging',
      'protingent',
      'Wood Personnel',
      'Wood Personnel (Adapt)',
      'Zee',
      'staffing-the-universe',
    ].includes(agency.name)
  ) {
    args.minRating = 1;
    args.maxRating = 5;
  }
  if (['adecco', 'agency-1'].includes(agency.name)) {
    args.minRating = 1;
    args.maxRating = 4;
  }
  return {
    rating_scale_survey_question: ratingScaleQuestion(
      args.minRating,
      args.maxRating,
    ),
    nps_survey_question: npsQuestion,
    multiple_choice_survey_question: multipleChoiceQuestion,
    //checkboxes_survey_question: checkboxesQuestion,
    // @ts-expect-error - TS2322 - Type '{ readonly type: "text_survey_question"; readonly question: ""; readonly alertKeywords: readonly []; }' is not assignable to type 'Question | undefined'.
    text_survey_question: textQuestion,
    message_survey_module: rhetoricalQuestion,
    beefree_module: beeFreeModule,
    sms_message_survey_module: smsMessageModule,
    // @ts-expect-error - TS2322 - Type '{ readonly type: "attribute_list_module"; readonly attributesToInclude: readonly []; }' is not assignable to type 'Question | undefined'.
    attribute_list_module: attributeListModule,
    field_writeback_module: textQuestion,
    // @ts-expect-error - TS2322 - Type '{ readonly type: "calendar_date_survey_question"; readonly question: ""; readonly alertKeywords: readonly []; readonly dateFormat: "yyyy-mm-dd"; }' is not assignable to type 'Question | undefined'.
    calendar_date_survey_question: calendarDateQuestion,
  };
};

export const ratingScaleQuestion = (
  minRating: number = 1,
  maxRating: number = 10,
  // @ts-expect-error - TS2322 - Type '{ type: "rating_scale_survey_question"; question: string; maxText: string; minText: string; minRating: number; maxRating: number; branchIds: never[]; }' is not assignable to type 'RatingScaleQuestion'.
): RatingScaleQuestion => ({
  type: 'rating_scale_survey_question',
  question: '',
  maxText: '',
  minText: '',
  minRating,
  maxRating,
  branchIds: [],
});

// @ts-expect-error - TS2322 - Type '{ type: "nps_survey_question"; question: string; maxText: string; minText: string; minRating: number; maxRating: number; branchIds: never[]; }' is not assignable to type 'NPSSurveyQuestion'.
export const npsQuestion: NPSSurveyQuestion = {
  type: 'nps_survey_question',
  question:
    'How likely are you to recommend [COMPANY NAME] to a friend or colleague?',
  maxText: 'Extremely likely',
  minText: 'Not likely at all',
  minRating: 0,
  maxRating: 10,
  branchIds: [],
};

export const rhetoricalQuestion: RhetoricalQuestion = {
  type: 'message_survey_module',
  text: '',
  include_job_block: false,
  job_matches_count: 5,
  send_alt_text: false,
  // @ts-expect-error - TS2322 - Type '{ city: null; state: null; keywords: null; title: null; skills: null; }' is not assignable to type 'JobMatchParams'.
  job_matches_params: {
    city: null,
    state: null,
    keywords: null,
    title: null,
    skills: null,
  },
};

export const beeFreeModule: BeeFreeModule = {
  type: 'beefree_module',
  beefree_html: '',
  // @ts-expect-error - TS2322 - Type 'null' is not assignable to type 'BeeFreeJson'.
  beefree_json: null,
};

// @ts-expect-error - TS2739 - Type '{ type: "sms_message_survey_module"; text: string; }' is missing the following properties from type 'SMSRhetoricalQuestion': id, required
export const smsMessageModule: SMSRhetoricalQuestion = {
  type: 'sms_message_survey_module',
  text: '',
};

const choices = fillArray(4, () => ({
  id: uniqueId('choice_'),
  value: '',
}));

// @ts-expect-error - TS2322 - Type '{ type: "multiple_choice_survey_question"; question: string; required: false; choices: { id: string; value: string; }[]; branchIds: never[]; alertChoices: never[]; }' is not assignable to type 'MultipleChoiceQuestion'.
const multipleChoiceQuestion: MultipleChoiceQuestion = {
  type: 'multiple_choice_survey_question',
  question: '',
  required: false,
  choices,
  branchIds: [],
  alertChoices: [],
};

const checkboxesQuestion = {
  type: 'checkboxes_survey_question',
  question: '',
  choices,
  branchIds: [],
  min: null,
  max: choices.length,
} as const;

const textQuestion = {
  type: 'text_survey_question',
  question: '',
  alertKeywords: [],
} as const;

const calendarDateQuestion = {
  type: 'calendar_date_survey_question',
  question: '',
  alertKeywords: [],
  dateFormat: 'yyyy-mm-dd',
} as const;
// TODO (nelson): Get rid of pre-filled attribute
const attributeListModule = {
  type: 'attribute_list_module',
  attributesToInclude: [],
} as const;
